import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  AllPlatformsForTemplateBuilderQuery,
  GetStructureDetailsDocument,
  useAllPlatformsForTemplateBuilderQuery,
  useUpdateStructureMutation,
} from '@/__generated__/graphql';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import Warning from '@mui/icons-material/Warning';
import Edit from '@mui/icons-material/Edit';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parse } from 'date-fns';
import { selectedStructure } from '../state';
import { StructureImg } from '../styles';
import { useSnackBarMessage } from '../utils';
import {
  DropDownContainer,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  tooltipStyles,
} from './styles';
import {
  Features,
  FeatureSection,
  FeatureValueMap,
  InsightsFeature,
  ViewerFeature,
} from '../types';
import { INSIGHTS_OPTIONS, StructureSection, VIEWER_OPTIONS } from '../constants';

type StructureTemplateFormProps = {
  structureId: string;
};

type Structure = {
  scanDate: string | null;
  isActive: boolean;
  isWallLossEnabled: boolean;
  platform: string | null;
  hasRiskAssessment: boolean;
  features: Features;
};

export const StructureTemplateForm = ({ structureId }: StructureTemplateFormProps) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const structure = useAtomValue(selectedStructure);
  const [localFields, setLocalFields] = useState<Structure | null>(null);
  const [allPlatforms, setAllPlatforms] = useState<
    AllPlatformsForTemplateBuilderQuery['allPlatforms']
  >([]);
  const [selectedField, setSelectedField] = useState<StructureSection>(
    StructureSection.StructureDetails
  );

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data: platformsData } = useAllPlatformsForTemplateBuilderQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (platformsData?.allPlatforms) {
      setAllPlatforms(platformsData.allPlatforms);
    }
  }, [platformsData?.allPlatforms]);

  const [updateStructureDetails, { loading: isLoading }] = useUpdateStructureMutation({
    refetchQueries: [GetStructureDetailsDocument],
  });

  useEffect(() => {
    if (!structure) return;

    const fieldsOnly = Object.fromEntries(
      Object.entries(structure).filter(
        ([key]) => !['id', 'name', 'coverImagePath', '__typename'].includes(key)
      )
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { navigation, ...structureFeatures } = structure.features;

    setLocalFields({
      ...fieldsOnly,
      scanDate: structure.scanDate ?? '',
      isActive: structure.isActive ?? false,
      platform: structure.platform?.name ?? '',
      isWallLossEnabled: structure.isWallLossEnabled ?? false,
      hasRiskAssessment: structure.hasRiskAssessment ?? false,
      features: structureFeatures,
    });
  }, [structure, mode]);

  const handleSaveStructure = useCallback(() => {
    if (!localFields) return;

    updateStructureDetails({
      variables: {
        input: {
          structureId,
          scanDate: localFields.scanDate,
          isActive: localFields.isActive,
          isWallLossEnabled: localFields.isWallLossEnabled,
          platformId: allPlatforms.find(
            (platform) => platform.name.toLowerCase() === localFields.platform?.toLowerCase()
          )?.id,
          hasRiskAssessment: localFields.hasRiskAssessment,
          features: localFields.features,
          lastModifiedFor: 'Update Structure Details',
        },
      },
      onCompleted: (response) => {
        if (response.updateStructure) {
          showSnackBar('Structure updated successfully', 'success');
          setMode('ReadOnly');
        }
      },
      onError: (error) => {
        console.error(error);
        showSnackBar(`Failed to update structure - ${error.message}`, 'error');
      },
    });
  }, [allPlatforms, localFields, showSnackBar, structureId, updateStructureDetails]);

  const handleCancel = useCallback(() => {
    if (structure) {
      setLocalFields({
        ...structure,
        scanDate: structure.scanDate ?? null,
        isActive: structure.isActive ?? false,
        platform: structure.platform?.name ?? null,
        isWallLossEnabled: structure.isWallLossEnabled ?? false,
        hasRiskAssessment: structure.hasRiskAssessment ?? false,
        features: structure.features,
      });
    }
    setMode('ReadOnly');
  }, [structure]);

  const handleCheckboxChange = useCallback((section: FeatureSection, item: string = '') => {
    setLocalFields((prev) => {
      if (!prev) return prev;

      const updatedFeatures: Features = { ...prev.features };

      if (!item) {
        if (section in updatedFeatures) {
          delete updatedFeatures[section];
        } else {
          updatedFeatures[section] = [];
        }
      }

      return {
        ...prev,
        features: updatedFeatures,
      };
    });
  }, []);

  const handleNestedCheckboxChange = useCallback(
    <T extends keyof FeatureValueMap>(section: T, item: FeatureValueMap[T]) => {
      setLocalFields((prev) => {
        if (!prev) return prev;

        const updatedFeatures: Features = { ...prev.features };
        const existing = (updatedFeatures[section] ?? []) as FeatureValueMap[T][];
        const includes = existing.includes(item);

        const newItems = includes ? existing.filter((i) => i !== item) : [...existing, item];

        if (newItems.length === 0) {
          delete updatedFeatures[section];
        } else {
          updatedFeatures[section] = newItems as
            | (ViewerFeature[] & InsightsFeature[] & string[])
            | undefined;
        }

        return {
          ...prev,
          features: updatedFeatures,
        };
      });
    },
    []
  );

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          {structure?.name ?? 'Structure'}
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveStructure}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <Stack direction="row" width="100%" gap={2}>
        <FieldsList>
          <Stack gap={1}>
            <Field
              key={`${StructureSection.StructureDetails}-${structure?.id}`}
              selected={selectedField === StructureSection.StructureDetails}
              onClick={() => {
                setSelectedField(StructureSection.StructureDetails);
              }}
            >
              Structure Details
            </Field>
            <Field
              key={`${StructureSection.Features}-${structure?.id}`}
              selected={selectedField === StructureSection.Features}
              onClick={() => {
                setSelectedField(StructureSection.Features);
              }}
            >
              Features
            </Field>
          </Stack>
        </FieldsList>

        {localFields ? (
          <>
            {selectedField === StructureSection.StructureDetails && localFields && (
              <FieldPanel gap={1} direction="row">
                <Stack width="50%" gap={1}>
                  <Grid container maxWidth="60rem" spacing={4}>
                    <Grid size={6}>
                      <DropDownContainer>
                        <Autocomplete
                          fullWidth
                          disableCloseOnSelect
                          includeInputInList
                          autoHighlight
                          disabled={mode === 'ReadOnly'}
                          options={allPlatforms.map((platform) => platform.name)}
                          value={localFields.platform ?? ''}
                          onChange={(_event, value) =>
                            setLocalFields({ ...localFields, platform: value ?? '' })
                          }
                          noOptionsText="No platforms found"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Select Platform"
                              placeholder="Select platform"
                              fullWidth
                            />
                          )}
                        />
                      </DropDownContainer>
                    </Grid>

                    <Grid size={6}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!localFields.isActive}
                              disabled={mode === 'ReadOnly'}
                              onChange={(event) =>
                                setLocalFields({
                                  ...localFields,
                                  isActive: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Is Active Deployment?"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid size={6}>
                      <DatePicker
                        views={['year', 'month']}
                        label="Scan Date"
                        format="MMMM, yyyy"
                        value={
                          localFields.scanDate
                            ? parse(localFields.scanDate, 'MMMM, yyyy', new Date())
                            : null
                        }
                        onChange={(newDate) =>
                          setLocalFields({
                            ...localFields,
                            scanDate: newDate ? format(newDate, 'MMMM, yyyy') : '',
                          })
                        }
                        slotProps={{ textField: { fullWidth: true, placeholder: 'MMMM, yyyy' } }}
                        disabled={mode === 'ReadOnly'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!localFields.isWallLossEnabled}
                              disabled={mode === 'ReadOnly'}
                              onChange={(event) =>
                                setLocalFields({
                                  ...localFields,
                                  isWallLossEnabled: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Is Wall Loss Enabled?"
                        />
                      </FormGroup>

                      <Typography fontSize="1.3rem" fontWeight={400}>
                        <Warning sx={{ fontSize: '1.4rem', color: abyssColors.warning[500] }} />
                        <span>
                          To update existing assembly calculations, go to the Assembly tab and click
                          MI Recalculation
                        </span>
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <FormGroup>
                        <Tooltip
                          title="Enable ES Sheet"
                          placement="bottom"
                          arrow
                          slotProps={{
                            popper: { sx: tooltipStyles },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!localFields.hasRiskAssessment}
                                disabled={mode === 'ReadOnly'}
                                onChange={(event) =>
                                  setLocalFields({
                                    ...localFields,
                                    hasRiskAssessment: event.target.checked,
                                  })
                                }
                              />
                            }
                            label="Has Risk Assessment?"
                          />
                        </Tooltip>
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Stack>

                <Stack
                  width="50%"
                  gap={1}
                  sx={{
                    background: abyssColors.primary[100],
                    padding: 4,
                    gap: '16px',
                    alignItems: 'center',
                  }}
                >
                  <Grid container maxWidth="60rem" spacing={4}>
                    <Grid size={12}>
                      <StructureImg>
                        <img
                          style={{ width: '100%' }}
                          src={
                            structure?.coverImagePath
                              ? getCloudfrontUrl(structure.coverImagePath)
                              : FALLBACK_INSPECTION_IMAGE
                          }
                          alt={structure?.name}
                          onError={(error) => (error.currentTarget.src = FALLBACK_INSPECTION_IMAGE)}
                          crossOrigin="use-credentials"
                        />
                      </StructureImg>
                    </Grid>

                    <Grid size={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled
                        startIcon={<FileUploadOutlined />}
                        onClick={() => {}}
                      >
                        Upload File
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </FieldPanel>
            )}

            {selectedField === StructureSection.Features && localFields.features && (
              <FieldPanel gap={1} direction="row">
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={12}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6">Insights</Typography>
                      <FormGroup>
                        {INSIGHTS_OPTIONS.map(({ label, key }) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={localFields.features.insights?.includes(key) || false}
                                onChange={() => handleNestedCheckboxChange('insights', key)}
                                disabled={mode === 'ReadOnly'}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Risk Assessment
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'riskAssessment' in localFields.features}
                              onChange={() => handleCheckboxChange('riskAssessment', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Risk Assessment"
                        />
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Reports
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'reports' in localFields.features}
                              onChange={() => handleCheckboxChange('reports', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Reports"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6">Viewer</Typography>
                      <FormGroup>
                        {VIEWER_OPTIONS.map(({ label, key }) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={localFields.features.viewer?.includes(key) || false}
                                onChange={() => handleNestedCheckboxChange('viewer', key)}
                                disabled={mode === 'ReadOnly'}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        IDMS
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'idms' in localFields.features}
                              onChange={() => handleCheckboxChange('idms', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="IDMS"
                        />
                      </FormGroup>

                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Deck Plan
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={'deckPlan' in localFields.features}
                              onChange={() => handleCheckboxChange('deckPlan', '')}
                              disabled={mode === 'ReadOnly'}
                            />
                          }
                          label="Deck Plan"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Box>
              </FieldPanel>
            )}
          </>
        ) : (
          <FieldPanel justifyContent="center" alignItems="center">
            <EmptyStatePlaceholder
              type="template"
              message="Click on the structure in the left panel to see its details"
            />
          </FieldPanel>
        )}
      </Stack>
    </Stack>
  );
};
